import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Languages, BookOpen, Users, Sparkles, ArrowRight, ChevronRight } from 'lucide-react';

const LandingPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading text-xl font-semibold">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button data-testid="nav-dashboard-btn" className="bg-primary hover:bg-primary/90">
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login-btn" className="text-foreground-muted hover:text-foreground">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="nav-register-btn" className="bg-primary hover:bg-primary/90">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1762279388988-3f8abcc7dca2?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="font-mono text-primary/80 text-sm tracking-wider uppercase">
                  Construct • Document • Share
                </p>
                <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight" data-testid="hero-title">
                  Build Languages<br />
                  <span className="text-primary">From Scratch</span>
                </h1>
              </div>
              
              <p className="text-lg text-foreground-muted max-w-xl leading-relaxed">
                The definitive platform for conlang creators. Design phonologies, 
                craft grammars, build lexicons, and document your constructed 
                languages with precision and elegance.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    data-testid="hero-get-started-btn"
                    className="bg-primary hover:bg-primary/90 text-lg px-8 py-6 glow-primary"
                  >
                    Start Creating
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    data-testid="hero-login-btn"
                    className="border-border-highlight hover:bg-accent text-lg px-8 py-6"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="hidden lg:block relative">
              <div className="relative rounded-lg overflow-hidden border border-border bg-background-paper">
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 text-foreground-subtle">
                    <div className="w-3 h-3 rounded-full bg-destructive" />
                    <div className="w-3 h-3 rounded-full bg-tertiary" />
                    <div className="w-3 h-3 rounded-full bg-quaternary" />
                  </div>
                  <div className="font-mono text-sm space-y-2">
                    <p><span className="text-secondary">// Phoneme Inventory</span></p>
                    <p><span className="text-primary">consonants</span>: <span className="text-tertiary">/p t k m n ŋ/</span></p>
                    <p><span className="text-primary">vowels</span>: <span className="text-tertiary">/a e i o u/</span></p>
                    <p className="mt-4"><span className="text-secondary">// Word Formation</span></p>
                    <p><span className="text-primary">root</span>: <span className="text-foreground">"tal"</span> → <span className="text-quaternary">water</span></p>
                    <p><span className="text-primary">prefix</span>: <span className="text-foreground">"ka-"</span> → <span className="text-quaternary">great</span></p>
                    <p><span className="text-primary">result</span>: <span className="text-foreground">"katal"</span> → <span className="text-quaternary">ocean</span></p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl animate-glow-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-16">
            <p className="font-mono text-secondary text-sm tracking-wider uppercase mb-4">Features</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
              Everything You Need to<br />
              <span className="text-tertiary">Construct Languages</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Feature Card 1 */}
            <div className="group p-6 bg-background-paper border border-border rounded-md hover:border-primary/50 transition-colors duration-300" data-testid="feature-phonology">
              <div className="w-12 h-12 rounded-md bg-secondary/20 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Phonology</h3>
              <p className="text-foreground-muted text-sm leading-relaxed">
                Define consonants, vowels, and phonotactic rules. Support for IPA transcription.
              </p>
            </div>
            
            {/* Feature Card 2 */}
            <div className="group p-6 bg-background-paper border border-border rounded-md hover:border-primary/50 transition-colors duration-300" data-testid="feature-grammar">
              <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Grammar</h3>
              <p className="text-foreground-muted text-sm leading-relaxed">
                Build morphological rules, syntax patterns, and grammatical structures.
              </p>
            </div>
            
            {/* Feature Card 3 */}
            <div className="group p-6 bg-background-paper border border-border rounded-md hover:border-primary/50 transition-colors duration-300" data-testid="feature-lexicon">
              <div className="w-12 h-12 rounded-md bg-tertiary/20 flex items-center justify-center mb-4">
                <Languages className="w-6 h-6 text-tertiary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Lexicon</h3>
              <p className="text-foreground-muted text-sm leading-relaxed">
                Create and manage your vocabulary with etymology tracking and semantic fields.
              </p>
            </div>
            
            {/* Feature Card 4 */}
            <div className="group p-6 bg-background-paper border border-border rounded-md hover:border-primary/50 transition-colors duration-300" data-testid="feature-documentation">
              <div className="w-12 h-12 rounded-md bg-quaternary/20 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-quaternary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Documentation</h3>
              <p className="text-foreground-muted text-sm leading-relaxed">
                Generate beautiful reference materials and grammar guides automatically.
              </p>
            </div>
            
            {/* Feature Card 5 */}
            <div className="group p-6 bg-background-paper border border-border rounded-md hover:border-primary/50 transition-colors duration-300" data-testid="feature-community">
              <div className="w-12 h-12 rounded-md bg-secondary/20 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Community</h3>
              <p className="text-foreground-muted text-sm leading-relaxed">
                Share your creations and collaborate with other language constructors.
              </p>
            </div>
            
            {/* Feature Card 6 - Coming Soon */}
            <div className="group p-6 bg-background-surface/50 border border-border/50 rounded-md" data-testid="feature-coming-soon">
              <div className="w-12 h-12 rounded-md bg-foreground-subtle/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-foreground-subtle" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground-muted mb-2">More Coming</h3>
              <p className="text-foreground-subtle text-sm leading-relaxed">
                Writing systems, translation tools, and AI-powered suggestions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
            Ready to Create Your Language?
          </h2>
          <p className="text-foreground-muted text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of conlang creators who are building unique languages for 
            fiction, linguistics research, and artistic expression.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              data-testid="cta-get-started-btn"
              className="bg-primary hover:bg-primary/90 text-lg px-10 py-6 glow-primary"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-4 h-4 text-primary" />
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-muted">
              LanguageConstructor
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-foreground-subtle hover:text-foreground text-sm transition-colors">
              About
            </Link>
            <Link to="/privacy" className="text-foreground-subtle hover:text-foreground text-sm transition-colors">
              Privacy
            </Link>
            <p className="text-foreground-subtle text-sm">
              © {new Date().getFullYear()} Gene Rohrbaugh. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
